import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import "./Modal.scss";

import UniversalButton from "components/uiComponents/UniversalButton";
import Select from "components/uiComponents/Select";
import UniversalInput from "components/uiComponents/UniversalInput";
import ToggleSwitch from "components/uiComponents/ToggleSwitch";
import ConfirmActionModal from "components/modals/ConfirmActionModal";

import { updateTable, setCustomColumn, editCustomColumn, deleteCustomColumn } from "store/actions/table";
import { updateFilter } from "store/actions/filter";
import { getTableColumns, getTemplateSetting, getCustomProperties } from "store/selectors/templateSettings.js";

import { valueTypes } from "utils/builder/ResultClasses";
import { updateCategory } from "utils/builder/IScheduleLogic";
import useSetting from "../../sidebarComponents/SidebarProject/use-settings";

import { ReactComponent as PlusCircleIcon } from "static/icons/plus-circle.svg";
import { ReactComponent as RemoveIcon } from "static/icons/cross-circle-gray.svg";
import { ReactComponent as DeleteIcon } from "static/icons/trash.svg";


function isParamTypeHasValues(paramType) {
  return paramType === valueTypes.customEnumMulty || paramType === valueTypes.customEnumSingle || paramType === valueTypes.proEnumSingle || paramType === valueTypes.proEnumMulty
}

function decideValues(selectedType, multipleChoiceValues) {
  if (isParamTypeHasValues(selectedType)) {
  return multipleChoiceValues?.length > 0 ? multipleChoiceValues : [""] // array with an empty string is default when ADDING new mc/sc param with no choices
  }
  return null;
}
function decideIsMulti(originColumn) {
  if (!originColumn?.type)
    return false
  return originColumn.type === valueTypes.customEnumMulty || originColumn.type === valueTypes.proEnumMulty
}

const AddParameterModal = (props) => {
  const { closeModal, isEdit, originColumn, deleteCallback, onCloseHandler } = props;

  const { t } = useTranslation();

  const options = useMemo(() => [
    {
      key: valueTypes.customText,
      value: t('text'),
    },
    {
      key: valueTypes.customNumber,
      value: t('number'),
    },
    {
      key: valueTypes.customDate,
      value: t('date'),
    },
    {
      key: valueTypes.customEnumMulty,
      value: t('multiple_choices'),
    },
    {
      key: valueTypes.customBool,
      value: t('checkbox'),
    },
    {
      key: valueTypes.customLink,
      value: t('link'),
    },
  ], [t]);

  const [isApplyClicked, setIsApplyClicked] = useState(false);

  const dispatch = useDispatch();
  const { addPropertyHandler } = useSetting()

  const tableColumns = useSelector(getTableColumns);
  const templateSettings = useSelector(getTemplateSetting)
  const customProperties = useSelector(getCustomProperties)

  const selectedGroups = templateSettings.groups ?? [];

  // Get actual column data from Redux store instead of relying on prop
  // Always get the latest data from store, not from prop
  const actualOriginColumn = useMemo(() => {
    if (!originColumn?.metaData?.name) {
      return originColumn;
    }
    
    // Always find by current name from store - this will have the latest type
    const foundColumn = tableColumns.find((column) => column.metaData.name === originColumn.metaData.name);
    
    // Return found column with latest data, or fallback to originColumn
    return foundColumn || originColumn;
  }, [originColumn?.metaData?.name, tableColumns]);

  const selectedProperties = useMemo(() => {
    return tableColumns.reduce(((acc, column) => {
      const { isCustomColumn, name } = column.metaData
      if (!isCustomColumn) {
        acc.push(name)
      }
      return acc
    }), [])
  },
    [tableColumns]);

  const [name, setName] = useState("");
  const [currentOption, setCurrentOption] = useState(null);
  const [multipleChoiceValues, setMultipleChoiceValues] = useState([""]);
  const [isMulti, setIsMulti] = useState(false);
  const [isUserChangedType, setIsUserChangedType] = useState(false);

  useEffect(() => {
    function getCustomParamValues (columnName) {
      const customProperty = customProperties?.filter(cp => cp.name === columnName)

      return customProperty && customProperty.length ? customProperty[0].values : ['']
    } 

    if (actualOriginColumn && actualOriginColumn.metaData?.name) {
      const customParamName = actualOriginColumn?.metaData?.name
      const customParamType = actualOriginColumn?.type
      const filteredOptions = options.find(item => item.key === customParamType)
      
      setName(customParamName || "");
      setCurrentOption(filteredOptions || options[0]);
      setIsMulti(decideIsMulti(actualOriginColumn));
      setIsUserChangedType(false); // Reset when actualOriginColumn changes

      if (isParamTypeHasValues(customParamType)) {
        const values = getCustomParamValues(customParamName)
        setMultipleChoiceValues(values)
      } else {
        setMultipleChoiceValues([""]);
      }

    };
  }, [actualOriginColumn?.type, actualOriginColumn?.metaData?.name, actualOriginColumn?.metaData?.isCustomColumn, options, customProperties]);

  const handleAdditionalInputChange = (index, value) => {
    const updatedValues = [...multipleChoiceValues];
    updatedValues[index] = value;
    setMultipleChoiceValues(updatedValues);
  };

  const handleAddAdditionalInputClick = () => {
    const newField = "";
    const multipleChoiceValuesCopy = [...multipleChoiceValues];
    multipleChoiceValuesCopy.push(newField);

    setMultipleChoiceValues(multipleChoiceValuesCopy);
  };

  const handleDeleteAdditionalInputClick = (indexToRemove) => {
    const multipleChoiceValuesCopy = [...multipleChoiceValues].filter(
      (_, index) => index !== indexToRemove
    );
    setMultipleChoiceValues(multipleChoiceValuesCopy);
  };

  const dispatchFilters = (callback, type, ...data) => {
    dispatch(
      updateFilter(
        () => callback(...data),
        type
      )
    );
  };

  const handleChangeOptions = (option, optionsType) => {
    const updateCategoryRequest = (currentOptions) => {
      if (optionsType !== "categories") {
        return
      }
      dispatchFilters(updateCategory, "CATEGORY", currentOptions);
    }
    addPropertyHandler(option, optionsType, updateCategoryRequest)
  };

  const onDeleteHandler = () => {
    dispatch(deleteCustomColumn(name));

    const updateReport = async () => {
      const filteredSelectedProperties = selectedProperties.filter(item => item !== name);
      await dispatch(updateTable([...filteredSelectedProperties], selectedGroups));
    };

    updateReport();
    closeModal();
  };

  const applyCustomColumn = async () => {
    if (!!name && !!currentOption) {

      // Use original values from prop, not from actualOriginColumn
      // because actualOriginColumn might have already been updated
      const prevType = originColumn?.type
      const prevName = originColumn?.metaData?.name;
      let selectedType = currentOption?.key;

      if (currentOption?.key === valueTypes.customEnumMulty || prevType === valueTypes.customEnumSingle) {
        selectedType = isMulti ? valueTypes.customEnumMulty : valueTypes.customEnumSingle
      };

      const values = decideValues(selectedType, multipleChoiceValues)
      
      if (isEdit) {
        await dispatch(
          editCustomColumn(prevName, name, prevType, selectedType, values)
        );
        // Update table immediately after editing to apply changes
        await dispatch(updateTable(selectedProperties, selectedGroups));
      } else {
        dispatch(setCustomColumn(name, selectedType, values));
        handleChangeOptions(name, 'properties')
      };

      closeModal();
      if (isEdit) {
        onCloseHandler();
      };
    };
  };
  
  const modalFields = useMemo(() => [
    {
      fieldName: t('column_name'),
      field: (
        <UniversalInput
          type="text"
          onChangeValue={(value) => {
            setName(value);
          }}
          value={name}
          isClearButtonActive
          onClearSearch={() => {
            setName("");
          }}
        />
      ),
    },
    {
      fieldName: t('type'),
      field: (
        <Select
          key={currentOption?.key}
          selectedValue={currentOption}
          setSelectedValue={(option) => {
            setIsUserChangedType(true);
            setCurrentOption(option);
          }}
          isBold={false}
          selectSize={"md"}
          isDropdownInModal
          withAbsolutePosition
          customContainerClass="selectDropDownContainer"
          options={options}
        />
      ),
    },
  ], [name, currentOption, options, t]);

  if (isApplyClicked && isEdit) {
    return <ConfirmActionModal
      title={t('edit_parameter')}
      description={t('edit_parametr_description')}
      confirmButtonText={t('yes_continue')}
      confirmCallback={applyCustomColumn}
      onClose={() => setIsApplyClicked(false)}
    />
  };

  return (
    <div className="modal">
      <span className="modal__header">{isEdit ? t('edit_parameter') : t('add_parameter')}</span>
      <div className="modal__content">
        {modalFields.map((item, i) => (
          <div key={item.fieldName + i}>
            <p className="modal__content-field-title">{item.fieldName}</p>
            {item.field}
          </div>
        ))}

        { (originColumn?.type === valueTypes.customEnumSingle || currentOption?.key === valueTypes.customEnumMulty) && (
          <div>
            <div className="add-parametr-multi-toggle">
              <div className="add-parametr-multi-toggle__title">
                {t('multiselect_values')}
              </div>

              <ToggleSwitch
                small
                onChange={setIsMulti}
                checked={isMulti}
                id="parametr-multi-toggle"
              />
            </div>

            <p className="modal__content-field-title">{t('values')}</p>
            <div className="modal_additional-section">
              {multipleChoiceValues?.map((field, index) => (
                <div className="modal_additional-section__field" key={`multiple-choice-values${index}`}>
                  <UniversalInput
                    type="text"
                    onChangeValue={(value) => {
                      handleAdditionalInputChange(index, value);
                    }}
                    value={field}
                    isClearButtonActive
                    onClearSearch={() => {
                      handleAdditionalInputChange(index, "");
                    }}
                  />

                  <div
                    className="modal_additional-section__field-delete-icon"
                    onClick={() => handleDeleteAdditionalInputClick(index)}
                  >
                    <RemoveIcon />
                  </div>
                </div>
              ))}

              <UniversalButton
                leftIcon={<PlusCircleIcon />}
                labelColor="blue"
                onClick={handleAddAdditionalInputClick}
                backgroundHoverColor="blue-light"
                backgroundColor="blue"
                isFullWidth
                label={t('add_value')}
              />
            </div>
          </div>
        )}
      </div>

      <div className={!!isEdit ? "modal_btns" : "modal__footer"}>
        {!!isEdit &&
          <div className="modal-delete-btn">
            <UniversalButton
              leftIcon={<DeleteIcon />}
              type="button"
              label={t('delete')}
              size="lg"
              labelColor="blue"
              labelHoverColor="blue"
              backgroundColor="white"
              backgroundHoverColor="blue-light"
              onClick={() => deleteCallback(name, onDeleteHandler)}
            />
          </div>
        }
        <div className={!isEdit ? "modal__footer__controls" : "modal__footer__row-controls"}>
          <UniversalButton
            type="button"
            label={t('cancel')}
            size="lg"
            labelColor="blue"
            labelHoverColor="blue"
            backgroundColor="white"
            backgroundHoverColor="blue-light"
            onClick={closeModal}
          />

          <UniversalButton
            type="button"
            label={t('apply')}
            isDisabled={!name || !currentOption}
            size="lg"
            labelColor="white"
            labelHoverColor="white"
            backgroundColor="orange"
            backgroundHoverColor="orange-light"
            onClick={isEdit ? () => setIsApplyClicked(true) : applyCustomColumn}
          />
        </div>
      </div>
    </div>
  );
};

export default AddParameterModal;
